import React from 'react';
import Button from '../ui/Button';

function getAlertTone(level) {
  const normalized = String(level || '').toLowerCase();
  if (normalized === 'critical') {
    return 'danger';
  }
  if (normalized === 'concern') {
    return 'warning';
  }
  if (normalized === 'watch') {
    return 'info';
  }
  return 'neutral';
}

function getTrendTone(trend) {
  const normalized = String(trend || '').toLowerCase();
  if (normalized === 'improving') {
    return 'good';
  }
  if (normalized === 'declining') {
    return 'danger';
  }
  return 'neutral';
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

function SortableHeader({ label, column, sortColumn, sortDirection, onSort }) {
  const active = sortColumn === column;
  return (
    <th>
      <button
        type="button"
        className={`director-sort-button ${active ? 'active' : ''}`}
        onClick={() => onSort(column)}
      >
        {label}
        <span>{active ? (sortDirection === 'asc' ? '^' : 'v') : '<>'}</span>
      </button>
    </th>
  );
}

function renderGroupLabel(groupBy) {
  if (groupBy === 'supervisor') {
    return 'Supervisor';
  }
  if (groupBy === 'location') {
    return 'Location';
  }
  return 'Person';
}

function AttendanceRiskTable({
  groupBy = 'none',
  rows = [],
  selectedWindowDays,
  sortColumn,
  sortDirection,
  onSort,
  onSelectRow,
  selectedRowKey,
  onView,
  onLogEvent,
  onAddPto
}) {
  const isGrouped = groupBy !== 'none';

  return (
    <div className="table-card">
      <table className="role-table attendance-risk-table">
        <thead>
          <tr>
            <SortableHeader label={renderGroupLabel(groupBy)} column="displayLabel" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            {isGrouped ? (
              <>
                <SortableHeader label="People" column="employeeCount" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
                <SortableHeader label="Roster-Linked" column="managerBackedEmployeeCount" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
                <SortableHeader label="Standalone" column="standaloneEmployeeCount" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
                <SortableHeader label="Critical" column="criticalCount" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
                <SortableHeader label="Concern" column="concernCount" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
                <SortableHeader label="Watch" column="watchCount" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
                <SortableHeader label="Discipline Review" column="disciplineReviewCount" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
                <SortableHeader label="Average At Work %" column="averageAtWorkRate" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
                <SortableHeader label="Lowest At Work %" column="lowestAtWorkRate" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
              </>
            ) : (
              <>
                <SortableHeader label="At Work %" column="atWorkRate" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
                <SortableHeader label={`Sick (${selectedWindowDays}d)`} column="selectedWindowSick" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
                <SortableHeader label={`Absence (${selectedWindowDays}d)`} column="selectedWindowAbsence" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
                <SortableHeader label={`Late (${selectedWindowDays}d)`} column="selectedWindowLate" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
                <SortableHeader label="Total Incidents (90d)" column="totalIncidents" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
                <SortableHeader label="Trend" column="trend" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
                <SortableHeader label="Alert" column="alertLevel" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
                <SortableHeader label="Discipline Flag" column="disciplineReviewFlag" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
              </>
            )}
            <th className="attendance-risk-col-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length ? rows.map((row) => {
            const alertTone = getAlertTone(isGrouped
              ? (Number(row.criticalCount || 0) > 0 ? 'Critical' : Number(row.concernCount || 0) > 0 ? 'Concern' : Number(row.watchCount || 0) > 0 ? 'Watch' : 'Normal')
              : row.alertLevel);
            return (
              <tr
                key={row.rowKey}
                className={`attendance-risk-row ${selectedRowKey === row.rowKey ? 'selected' : ''} ${alertTone}`}
                onClick={() => onSelectRow(row)}
              >
                <td className="attendance-risk-col-manager">
                  <div className="attendance-risk-manager-cell">
                    <strong>{row.displayLabel}</strong>
                    <span>{row.locationCode}</span>
                  </div>
                </td>
                {isGrouped ? (
                  <>
                    <td className="attendance-risk-col-metric">{row.employeeCount}</td>
                    <td className="attendance-risk-col-metric">{row.managerBackedEmployeeCount}</td>
                    <td className="attendance-risk-col-metric">{row.standaloneEmployeeCount}</td>
                    <td className="attendance-risk-col-metric">{row.criticalCount}</td>
                    <td className="attendance-risk-col-metric">{row.concernCount}</td>
                    <td className="attendance-risk-col-metric">{row.watchCount}</td>
                    <td className="attendance-risk-col-metric">{row.disciplineReviewCount}</td>
                    <td className="attendance-risk-col-metric">{formatPercent(row.averageAtWorkRate)}</td>
                    <td className="attendance-risk-col-metric">{formatPercent(row.lowestAtWorkRate)}</td>
                  </>
                ) : (
                  <>
                    <td className="attendance-risk-col-metric">{formatPercent(row.atWorkRate)}</td>
                    <td className="attendance-risk-col-metric">{row.selectedWindowSick}</td>
                    <td className="attendance-risk-col-metric">{row.selectedWindowAbsence}</td>
                    <td className="attendance-risk-col-metric">{row.selectedWindowLate}</td>
                    <td className="attendance-risk-col-metric">{row.totalIncidents}</td>
                    <td className="attendance-risk-col-trend">
                      <span className={`role-status-pill ${getTrendTone(row.trend)}`}>{row.trend}</span>
                    </td>
                    <td className="attendance-risk-col-alert">
                      <span className={`role-status-pill ${alertTone}`}>{row.alertLevel}</span>
                    </td>
                    <td className="attendance-risk-col-discipline">{row.disciplineReviewFlag ? <span className="role-status-pill warning">{row.disciplineReviewFlag}</span> : '-'}</td>
                  </>
                )}
                <td className="attendance-risk-col-actions">
                  <div className="director-table-actions" onClick={(event) => event.stopPropagation()}>
                    <Button size="sm" variant="secondary" onClick={() => onView(row)}>
                      {isGrouped ? 'View People' : 'Open Workspace'}
                    </Button>
                    {!isGrouped ? (
                      <>
                        <Button size="sm" variant="secondary" onClick={() => onLogEvent(row)}>Log Event</Button>
                        <Button size="sm" variant="secondary" onClick={() => onAddPto(row)}>Add PTO</Button>
                      </>
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          }) : (
            <tr>
              <td colSpan={isGrouped ? 11 : 10}>No attendance risk rows match the current scope and filters.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default AttendanceRiskTable;
